import { useCallback, useEffect } from "react";

import { useDispatch } from "react-redux";

import { SuccessToastContent } from "@similarweb/ui-components";

import { hideToastsByTag, showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";

/** Tags every toast this module raises, so leaving the page can retract the ones still standing. */
export const SIGNAL_RECOMMENDER_TOAST_TAG = "signal-recommender";

/** `makeShowToast` only schedules its hide timer when the timeout is positive, so 0 means "stays put". */
const NO_AUTO_HIDE = 0;

type SignalRecommenderToasts = {
    readonly showSuccess: (text: string) => void;
    readonly showError: (text: string) => void;
};

/**
 * Enrichment outcomes stay until dismissed. A run takes minutes and reports back through a poll, so the
 * reader is rarely watching when it lands — a toast on a timer would announce the result to an empty room.
 *
 * That makes the unmount sweep load-bearing rather than tidy: nothing else retracts a toast with no timer,
 * and it would otherwise sit over every other page until a reload.
 */
export const useSignalRecommenderToasts = (): SignalRecommenderToasts => {
    const dispatch = useDispatch();

    const showSuccess = useCallback(
        (text: string) => {
            dispatch(
                showSuccessToast(
                    <SuccessToastContent>{text}</SuccessToastContent>,
                    undefined,
                    NO_AUTO_HIDE,
                    undefined,
                    SIGNAL_RECOMMENDER_TOAST_TAG,
                ),
            );
        },
        [dispatch],
    );

    const showError = useCallback(
        (text: string) => {
            dispatch(
                showErrorToast(
                    renderErrorToast({ content: text, minWidth: "auto" }),
                    undefined,
                    NO_AUTO_HIDE,
                    undefined,
                    SIGNAL_RECOMMENDER_TOAST_TAG,
                ),
            );
        },
        [dispatch],
    );

    useEffect(
        () => () => {
            dispatch(hideToastsByTag(SIGNAL_RECOMMENDER_TOAST_TAG));
        },
        [dispatch],
    );

    return { showSuccess, showError };
};
